import React, { useMemo } from "react"
import PropTypes from "prop-types"
import { useSelector, useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import SKUHorizontalTile from "@components/SKUTile/SKUHorizontalTile/SKUHorizontalTile"
import LabSearchTile from "@components/LabSearchTile/LabSearchTile"
import TextualButton from "@components/Buttons/TextualButton/TextualButton"
import { addRecentSearch } from "@containers/LabsSearch/reducer"
import useCartAPI from "@flows/AddToCart/actions"
import { trackRemoveTest, trackAboutTest, trackAboutLab } from "@containers/LabsSearch/tracking"
import css from "./ListOfHorizonalTiles.scss"
import { sendMixpanelTrackEvent } from "@lib/gtmUtility"
import { DIAGNOSTICS } from "@constants"

function ListOfHorizontalTiles(props) {
    const {
        type: widgetType,
        title,
        limit,
        cta,
        ctaHandler,
        results,
        searchTerm,
        source,
        widgetMixPanel,
    } = props

    const { itemsInLabsCart, cartId, pageName } = useSelector((state) => state.shellReducer)

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { initiateCartAction } = useCartAPI()
    const city = useSelector((state) => state.locationsReducer.currentCity)

    const selectedSKUIds = useMemo(() => {
        return Object.keys(itemsInLabsCart)
    }, [itemsInLabsCart])

    const addSelectedSKU = (sku) => {
        const payload = {
            city,
            response_type: "brief",
            types: "labs",
            sku_ids: [
                {
                    test_id: sku?.id,
                },
            ],
            sku: sku,
        }

        initiateCartAction(
            "add_labs_cart_item",
            payload,
            (atcResponse) => {
                const eventName = atcResponse?.brief?.is_partial_cart ? "sku_added" : "sku_added_to_cart"
                const mixPanelData = {
                    ...widgetMixPanel,
                    ...sku.mixPanelData,
                    business_unit: DIAGNOSTICS,
                    page_name: "search_suggestions_page",
                    lab_id: atcResponse?.brief?.lab_id,
                    lab_name: atcResponse?.brief?.lab_name,
                }
                sendMixpanelTrackEvent(eventName, mixPanelData)
            },
            null
        )
    }

    const removeSelectedSKU = (sku) => {
        const payload = {
            cartId: cartId,
            sku_ids: [{ test_id: sku.id }],
            source: "search bar",
            response_type: "brief",
            types: "labs",
        }
        initiateCartAction(
            "delete_labs_cart_item",
            payload,
            () => {
                sendMixpanelTrackEvent("add_more_quantity_clicked", {
                    business_unit: DIAGNOSTICS,
                    cta_source: "sku_widget",
                    cta_type: "remove",
                    page_name: "search_suggestions_page",
                    ...widgetMixPanel,
                    ...sku.mixPanelData,
                })
            },
            null
        )
    }

    return (
        <div>
            {title && (
                <div className={` smallMedium ${css.header}`}>
                    <div className="textTertiary">{title}</div>
                    {cta && <TextualButton variantSmall label={cta.text} onClick={handleButtonClick} />}
                </div>
            )}
            {results && <div className="">{renderTiles(results, searchTerm, source)}</div>}
        </div>
    )

    function handleButtonClick(e) {
        e.preventDefault()
        ctaHandler?.()
    }

    function handleTestClick(e, result, slug, widgetType) {
        dispatch(addRecentSearch(result))
        trackAboutTest(`${result.name} | ${result.id} | ${widgetType}`)
        const isDidYouMean = widgetType === "fuzzy_search"
        const isGeneral = widgetType === "search"
        sendMixpanelTrackEvent("search_suggestion_clicked", {
            business_unit: DIAGNOSTICS,
            page_name: pageName,
            searched_term: searchTerm,
            selected_search: result.name,
            searched_source: isDidYouMean ? "did_you_mean" : "general",
            selected_search_type: isDidYouMean ? "did_you_mean" : isGeneral ? "general" : widgetType,
            ...result.mixPanelData,
        })
        window.location.href = slug
    }

    function handleLabClick(e, result, slug, widgetType) {
        dispatch(addRecentSearch(result))
        trackAboutLab(`${result.name} | ${widgetType}`)

        navigate(slug)
    }

    function renderTiles(results, searchTerm = "", source = null) {
        return results.slice(-limit).map((result, index) => {
            result = result || {}

            const {
                id,
                image,
                name,
                line1,
                line2,
                line3,
                line4,
                tags,
                rating,
                price,
                url: slug,
                type,
                category,
            } = result

            switch (type) {
                case "test": {
                    const gaData = {
                        label: {
                            source: source || "labs",
                            search_term: searchTerm,
                            text: [line1, line2, line3, line4].filter(Boolean).join("_"),
                            type: widgetType,
                            selected_search: name,
                            position: index + 1,
                        },
                        category: "Diagnostics Search Page",
                    }
                    const testProps = {
                        isChecked: selectedSKUIds.includes(id.toString()),
                        image,
                        line1,
                        line2,
                        line3,
                        line4,
                        tags: tags?.map(({ text, textColor, bgColor, borderColor }) => ({
                            label: text,
                            color: textColor,
                            backgroundColor: bgColor,
                            borderColor,
                        })),
                        rating,
                        pricing: price?.mrp && {
                            price: price?.mrp,
                            discountedPrice: price?.discountedPrice,
                            discountedPercent: price?.discount,
                        },
                        iconAddHandler: () => {
                            addSelectedSKU(result)

                            if (widgetType !== "recent_searches") {
                                dispatch(addRecentSearch(result))
                            }
                        },
                        iconCheckedHandler: () => {
                            const removeTestLabel = {
                                ...gaData.label,
                                entity_id: id,
                                entity_name: name,
                                entity_type: category,
                                entity_sub_type: type,
                            }
                            trackRemoveTest(removeTestLabel, gaData.category, "Remove test")
                            removeSelectedSKU(result)
                        },
                    }

                    return (
                        <div
                            key={id}
                            className={css.tile}
                            onClick={(e) => handleTestClick(e, result, slug, widgetType)}
                        >
                            <SKUHorizontalTile {...testProps} />
                        </div>
                    )
                }

                case "lab": {
                    const labProps = {
                        image,
                        line1,
                        line2,
                        line3,
                        line4,
                        tags: tags?.map(({ text, textColor, bgColor, borderColor }) => ({
                            label: text,
                            color: textColor,
                            backgroundColor: bgColor,
                            borderColor,
                        })),
                        rating,
                        showChevron: true,
                    }

                    return (
                        <div
                            key={id}
                            className={css.tile}
                            onClick={(e) => handleLabClick(e, result, slug, widgetType)}
                        >
                            <LabSearchTile {...labProps} />
                        </div>
                    )
                }

                default:
                    return null
            }
        })
    }
}

ListOfHorizontalTiles.propTypes = {
    pageName: PropTypes.string,
    type: PropTypes.string,
    title: PropTypes.string,
    limit: PropTypes.number,
    cta: PropTypes.shape({
        text: PropTypes.string,
        action: PropTypes.string,
        slug: PropTypes.string,
    }),
    ctaHandler: PropTypes.func,
    results: PropTypes.arrayOf(
        PropTypes.shape({
            type: PropTypes.string,
            data: PropTypes.object,
        })
    ),
    searchTerm: PropTypes.string,
    source: PropTypes.string,
    resultType: PropTypes.string,
}
ListOfHorizontalTiles.defaultProps = {
    searchTerm: "",
    source: "labs",
}

export default ListOfHorizontalTiles
